import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import Helmet from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ReligiousNamingCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const analyzeNameOrigins = () => {
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    // Function to generate religious origin data
    const generateReligiousData = (inputName) => {
      const religions = ['Christian', 'Jewish', 'Islamic', 'Hindu', 'Buddhist'];
      const meanings = [
        'Hope', 'Strength', 'Wisdom', 'Beauty', 'Peace',
        'Joy', 'Faith', 'Victory', 'Light', 'Grace'
      ];

      // Generate religious distribution
      const religiousDistribution = [];
      let totalPercentage = 0;

      while (totalPercentage < 100) {
        const religion = religions[Math.floor(Math.random() * religions.length)];
        const percentage = Math.floor(Math.random() * 30) + 1;
        religiousDistribution.push({ religion, percentage });
        totalPercentage += percentage;
      }

      // Adjust last religion to make total 100%
      religiousDistribution[religiousDistribution.length - 1].percentage = 
        100 - (totalPercentage - religiousDistribution[religiousDistribution.length - 1].percentage);

      return {
        primaryReligion: religions[Math.floor(Math.random() * religions.length)],
        meaning: meanings[Math.floor(Math.random() * meanings.length)],
        religiousDistribution,
        historicalUsage: [
          { year: 'Ancient', usage: Math.floor(Math.random() * 20) + 5 },
          { year: 'Medieval', usage: Math.floor(Math.random() * 30) + 10 },
          { year: 'Renaissance', usage: Math.floor(Math.random() * 40) + 15 },
          { year: 'Modern', usage: Math.floor(Math.random() * 50) + 20 },
          { year: 'Contemporary', usage: Math.floor(Math.random() * 60) + 25 }
        ]
      };
    };

    const nameData = generateReligiousData(name);

    // Prepare chart data
    const religiousChartData = {
      labels: nameData.religiousDistribution.map(item => item.religion),
      datasets: [ {
        data: nameData.religiousDistribution.map(item => item.percentage),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      } ]
    };

    const usageChartData = {
      labels: nameData.historicalUsage.map(item => item.year),
      datasets: [ {
        label: 'Usage Frequency',
        data: nameData.historicalUsage.map(item => item.usage),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        fill: false
      } ]
    };

    setResults(nameData);
    setChartData({
      religious: religiousChartData,
      usage: usageChartData
    });
  };

  return (
    <div>
      <Helmet>
        <title>Religious Naming Calculator | Discover Religious Significance</title>
        <meta name="description" content="Explore the religious origins, meanings, and historical significance of names from around the world. Understand how names connect to different religions and their symbolic meanings." />
        <meta name="keywords" content="religious name calculator, name origins, cultural significance, historical usage" />
        <meta property="og:title" content="Religious Naming Calculator | Discover Religious Significance" />
        <meta property="og:description" content="Explore the religious origins, meanings, and historical significance of names from around the world. Understand how names connect to different religions and their symbolic meanings." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/religious-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">🌟</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Religious Naming Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Discover the Religious Significance of Names</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Explore the religious origins, meanings, and historical significance of names from around the world.
            Understand how names connect to different religions and their symbolic meanings.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Enter a Name:
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter a name to analyze"
            />
          </div>

          <button
            onClick={analyzeNameOrigins}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Analyze Name Origins
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {results && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Religious Significance</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Primary Religion:</span>
                        <span className="font-medium">{results.primaryReligion}</span>
                      </div>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Name Meaning:</span>
                        <span className="font-medium">{results.meaning}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold mb-4">Religious Distribution</h4>
                  <div className="h-96">
                    <Pie
                      data={chartData.religious}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Religious Influence Distribution'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Historical Usage</h4>
                <div className="space-y-4">
                  <div className="h-96">
                    <Bar
                      data={chartData.usage}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: 'Time Period'
                            }
                          },
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Usage Frequency'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Name Usage Over Time'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-12">
            {/* How to Use Section */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Use</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-500 text-white">
                      1
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Enter a Name</h3>
                    <p className="mt-2 text-gray-600">Type any name in the input field to analyze its religious origins and significance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-500 text-white">
                      2
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Click Analyze</h3>
                    <p className="mt-2 text-gray-600">Click the "Analyze Name Origins" button to get detailed insights about the name's religious connections.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-purple-500 text-white">
                      3
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">View Results</h3>
                    <p className="mt-2 text-gray-600">Explore the religious distribution, historical usage, and cultural significance of the name through interactive charts and detailed information.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features & Benefits Section */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Features & Benefits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Comprehensive Analysis</h3>
                  <p className="text-gray-600">Get detailed insights into the religious origins and cultural significance of names.</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Interactive Visualizations</h3>
                  <p className="text-gray-600">Explore religious distribution and historical usage through interactive charts.</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Cross-Cultural Insights</h3>
                  <p className="text-gray-600">Discover connections across different religious traditions and their naming practices.</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Historical Context</h3>
                  <p className="text-gray-600">Learn about the evolution of names across different time periods and their usage patterns.</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Symbolic Meanings</h3>
                  <p className="text-gray-600">Understand the deeper symbolic and spiritual meanings associated with names.</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Educational Tool</h3>
                  <p className="text-gray-600">Use as a learning resource to explore cultural and religious naming traditions.</p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">What kind of names can I analyze?</h3>
                  <p className="text-gray-600">You can analyze any name, whether it's a personal name, surname, or even fictional names. The calculator will provide insights based on common religious naming patterns and historical usage.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">How accurate is the analysis?</h3>
                  <p className="text-gray-600">The analysis provides general patterns and common associations. While it's based on historical data and cultural patterns, individual names may have unique histories or meanings not captured by the analysis.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Can I use this for baby naming?</h3>
                  <p className="text-gray-600">Yes! This calculator can be a helpful tool when choosing a name by understanding its religious and cultural associations. However, remember that personal preference and family traditions are also important considerations.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">What religions are included in the analysis?</h3>
                  <p className="text-gray-600">The calculator analyzes connections to major world religions including Christianity, Judaism, Islam, Hinduism, and Buddhism, each with their unique naming traditions and symbolism.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Why is historical usage important?</h3>
                  <p className="text-gray-600">Understanding how a name has been used throughout history provides context about its cultural significance and how its meaning has evolved over time. This can be particularly interesting for names with long histories.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Is this analysis based on real data?</h3>
                  <p className="text-gray-600">The analysis is based on historical patterns and common naming practices across different cultures and religions. While it provides general insights, individual names may have unique stories and meanings not captured by the analysis.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligiousNamingCalculator;